import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

const R = 0.0821; // L·atm/(mol·K)

export default function IdealGasLawCalculator() {
  const [pressure, setPressure] = useState("");
  const [volume, setVolume] = useState("");
  const [moles, setMoles] = useState("");
  const [temperature, setTemperature] = useState("");

  const calculateMissing = () => {
    const p = pressure ? parseFloat(pressure) : null;
    const v = volume ? parseFloat(volume) : null;
    const n = moles ? parseFloat(moles) : null;
    const t = temperature ? parseFloat(temperature) : null;

    const filled = [p, v, n, t].filter(val => val !== null).length;

    if (filled !== 3) {
      toast.error("Please enter exactly 3 values");
      return;
    }

    if (p === null) {
      const result = (n! * R * t!) / v!;
      setPressure(result.toFixed(4));
    } else if (v === null) {
      const result = (n! * R * t!) / p!;
      setVolume(result.toFixed(4));
    } else if (n === null) {
      const result = (p! * v!) / (R * t!);
      setMoles(result.toFixed(4));
    } else if (t === null) {
      const result = (p! * v!) / (n! * R);
      setTemperature(result.toFixed(4));
    }

    toast.success("Calculation complete!");
  };

  const reset = () => {
    setPressure("");
    setVolume("");
    setMoles("");
    setTemperature("");
  };

  return (
    <PageLayout 
      calculatorName="Ideal Gas Law Calculator" 
      calculatorPath="/calculator/ideal-gas-law"
      category="chemistry"
      subcategory="Gases"
    >
      <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Ideal Gas Law Calculator</h1>
            <p className="text-muted-foreground">
              Calculate any variable using PV=nRT
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Formula: PV = nRT</CardTitle>
              <CardDescription>
                Enter any 3 values to calculate the 4th. R = 0.0821 L·atm/(mol·K)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pressure">Pressure (atm)</Label>
                  <Input
                    id="pressure"
                    type="number"
                    step="0.001"
                    placeholder="Leave blank to calculate"
                    value={pressure}
                    onChange={(e) => setPressure(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="volume">Volume (L)</Label>
                  <Input
                    id="volume"
                    type="number"
                    step="0.001"
                    placeholder="Leave blank to calculate"
                    value={volume}
                    onChange={(e) => setVolume(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="moles">Moles (mol)</Label>
                  <Input
                    id="moles"
                    type="number"
                    step="0.001"
                    placeholder="Leave blank to calculate"
                    value={moles}
                    onChange={(e) => setMoles(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="temperature">Temperature (K)</Label>
                  <Input
                    id="temperature"
                    type="number"
                    step="0.01"
                    placeholder="Leave blank to calculate"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculateMissing} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {(pressure || volume || moles || temperature) && (
                <CalculatorSuperCalcCTA config={calculatorCTAs["ideal-gas-law"]} trigger={pressure + volume + moles + temperature} />
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter any 3 of the 4 values</p>
                  <p>2. Leave the value you want to calculate blank</p>
                  <p>3. Click Calculate to find the missing value</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: P=1 atm, V=2 L, n=0.5 mol → T=48.7 K
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
